import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Input } from "../../components/Input/Input";
import { Select } from "../../components/Select/Select";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Table, type Column } from "../../components/Table/Table";
import { listBotActivity } from "./api";
import styles from "./BotsPage.module.css";
import type { BotAction } from "./types";

type Window = "1h" | "24h" | "7d";

/**
 * The Activity tab: what the bots actually did.
 *
 * This is the balance-debugging surface — the one screen that answers "is the
 * simulation alive and is it sane" without grepping logs. Kept for seven days,
 * then trimmed by the retention worker.
 */
export function ActivityTab({ projectID }: { projectID: string }) {
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [window, setWindow] = useState<Window>("24h");

  const { data, isLoading } = useQuery({
    queryKey: ["project", projectID, "bots", "activity", kind, q, window],
    queryFn: () => listBotActivity(projectID, { kind, q, window }),
    enabled: !!projectID,
    refetchInterval: 30_000,
  });

  const columns: Column<BotAction>[] = [
    {
      key: "time",
      header: "Time",
      width: "90px",
      cell: (a) => (
        <span className={styles.mono}>
          {new Date(a.created_at).toLocaleTimeString()}
        </span>
      ),
    },
    {
      key: "bot",
      header: "Bot",
      width: "190px",
      cell: (a) => (
        <div className={styles.who}>
          <Avatar name={a.bot_name} size={26} />
          <span className={styles.whoName}>{a.bot_name}</span>
        </div>
      ),
    },
    {
      key: "guild",
      header: "Guild",
      width: "170px",
      cell: (a) =>
        a.guild_id ? (
          <Link to={`/projects/${projectID}/guilds/${a.guild_id}`}>
            {a.guild_name}
          </Link>
        ) : (
          <span className={styles.free}>—</span>
        ),
    },
    {
      key: "kind",
      header: "Action",
      width: "150px",
      cell: (a) => (
        <Badge variant={KIND_VARIANT[a.kind] ?? "neutral"}>
          {a.kind.replace("_", " ")}
        </Badge>
      ),
    },
    { key: "detail", header: "Detail", cell: (a) => describeAction(a) },
  ];

  return (
    <>
      <div className={styles.toolbar}>
        <Select
          value={kind}
          onChange={setKind}
          options={[
            { value: "", label: "Action: all" },
            ...KINDS.map((k) => ({ value: k, label: k, mono: true })),
          ]}
        />
        <div className={styles.search}>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Bot name…"
            aria-label="Filter by bot name"
          />
        </div>
        <span className={styles.spacer} />
        <SegmentedToggle
          value={window}
          onChange={setWindow}
          aria-label="Time window"
          options={[
            { value: "1h", label: "1h" },
            { value: "24h", label: "24h" },
            { value: "7d", label: "7d" },
          ]}
        />
      </div>

      <div className={styles.card}>
        <Table
          columns={columns}
          rows={data?.actions ?? []}
          empty={
            isLoading
              ? "Loading…"
              : "Nothing in this window. If the simulation is on and this stays empty, the tick worker is not running."
          }
        />
        <div className={styles.cardFoot}>
          <span>
            {data
              ? `${data.actions.length} actions in the last ${window} · kept for 7 days`
              : "—"}
          </span>
          <span className={styles.mono}>bot_actions</span>
        </div>
      </div>
    </>
  );
}

const KINDS = [
  "chat",
  "welcome",
  "thanks",
  "reaction",
  "help_request",
  "donate",
  "join",
  "leave",
  "score",
  "score_refused",
  "backfill",
];

const KIND_VARIANT: Record<string, "accent" | "success" | "info" | "warn" | "neutral"> = {
  welcome: "accent",
  donate: "success",
  help_request: "info",
  join: "accent",
  leave: "warn",
  score_refused: "warn",
  backfill: "info",
};

/**
 * One line describing what happened. The feed shows the message text as well
 * as the category — an operator balancing a phrase library needs to see what
 * the bots are actually saying, not just how often.
 */
function describeAction(a: BotAction) {
  const d = a.detail ?? {};
  const text = typeof d.text === "string" ? d.text : null;
  const meta = [d.category, d.locale].filter(Boolean).join(" · ");
  switch (a.kind) {
    case "chat":
    case "reaction":
      return (
        <>
          “{text}” {meta && <span className={styles.mono}>· {meta}</span>}
        </>
      );
    case "welcome":
      return (
        <>
          Greeted a newcomer — “{text}”
        </>
      );
    case "thanks":
      return (
        <>
          Said thank you for <code>{String(d.request_type ?? "help")}</code> —
          “{text}”
        </>
      );
    case "donate":
      return (
        <>
          Sent <code>{String(d.request_type ?? "help")}</code> ×
          {String(d.amount ?? 1)}
          {typeof d.seconds_elapsed === "number"
            ? ` — ${d.seconds_elapsed} s after the request`
            : ""}
          {a.for_real_player ? " · to a real player" : ""}
        </>
      );
    case "help_request":
      // No expiry here: how long the request lives is the type's own
      // `help_request.types[].ttl_seconds`, which the bots section does not
      // know and this record never carried honestly.
      return (
        <>
          Asked for <code>{String(d.request_type ?? "help")}</code>
        </>
      );
    case "join":
      return (
        <>
          Joined — the guild was {String(d.below_target ?? 0)} members below the
          target
        </>
      );
    case "leave":
      if (d.reason === "kicked") return <>Left — kicked from the guild</>;
      return d.reason === "maturation" ? (
        <>
          Left — the guild reached {String(d.real_members ?? 0)} real members
          (maturation)
        </>
      ) : (
        <>Left — ordinary churn</>
      );
    case "score":
      return (
        <>
          Submitted <code>{String(d.ext_key ?? "")}</code> +
          {String(d.score ?? 0)}
        </>
      );
    case "score_refused":
      return (
        <>
          Could not submit <code>{String(d.ext_key ?? "")}</code> —{" "}
          <span className={styles.mono}>{String(d.reason ?? "unknown")}</span>
        </>
      );
    case "backfill":
      // `hours` is the stretch the written lines actually cover, not the
      // configured depth: the window is clipped to the guild's own age, and
      // lines dated before any bot had joined were dropped. So a young guild
      // gets a short past — and one whose history fits inside an hour gets a
      // figure that rounds to zero. "over 0 h" would read as a bug rather than
      // as the short history it is.
      return (
        <>
          Wrote {String(d.messages ?? 0)} messages back-dated over{" "}
          {Number(d.hours ?? 0) < 1 ? "under an hour" : `${String(d.hours)} h`} —
          first real visit
        </>
      );
    default:
      return <span className={styles.mono}>{JSON.stringify(d)}</span>;
  }
}
